import {
  Box,
  Button,
  Center,
  Heading,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useDropzone } from "react-dropzone";

/**
 * StepUpload Component
 * Uses react-dropzone for robust file handling and validation.
 * @param {Function} onUpload - Callback receiving the File object
 */
export default function StepUpload({ onUpload }) {
  // Define accepted file types for react-dropzone
  const accept = {
    "text/csv": [".csv"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
  };

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles?.length > 0) {
        onUpload(acceptedFiles[0]);
      }
    },
    [onUpload],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  // Dynamic colors based on state and theme
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const activeBorderColor = "blue.500";
  const rejectBorderColor = "red.500";

  const getBorderColor = () => {
    if (isDragReject || fileRejections.length > 0) return rejectBorderColor;
    if (isDragActive) return activeBorderColor;
    return borderColor;
  };

  const bg = useColorModeValue(
    isDragActive ? "blue.50" : "transparent",
    isDragActive ? "blue.900" : "transparent",
  );

  return (
    <VStack spacing={6} py={4}>
      <Box textAlign="center">
        <Heading size="md" color="gray.800" mb={1}>
          Upload your roster
        </Heading>
        <Text color="gray.500" fontSize="sm">
          Select a CSV or Excel file to get started.
        </Text>
      </Box>

      <Center
        {...getRootProps()}
        w="full"
        h="260px"
        px={6}
        border="2px dashed"
        borderColor={getBorderColor()}
        borderRadius="2xl"
        bg={bg}
        transition="all 0.2s ease"
        cursor="pointer"
        _hover={{ borderColor: activeBorderColor }}
      >
        <input {...getInputProps()} />

        <VStack spacing={4}>
          <Box fontSize="4xl">
            {isDragReject ? "❌" : isDragActive ? "📥" : "📂"}
          </Box>

          <VStack spacing={1}>
            <Text fontWeight="bold" textAlign="center" color="gray.700">
              {isDragActive
                ? "Drop it like it's hot"
                : "Drag & drop your roster here"}
            </Text>
            <Text fontSize="sm" color="gray.400">
              or click to browse files
            </Text>
          </VStack>

          {/* Validation Feedback */}
          {fileRejections.length > 0 && (
            <Text fontSize="xs" color="red.500" fontWeight="600">
              Invalid file. Please use .csv or .xlsx (max 50MB).
            </Text>
          )}

          <Button
            size="sm"
            variant="outline"
            colorScheme="blue"
            pointerEvents="none"
          >
            Choose File
          </Button>
        </VStack>
      </Center>

      <VStack spacing={1}>
        <Text fontSize="xs" color="gray.400">
          Supported formats: .csv, .xlsx, .xls, .json
        </Text>
        <Text fontSize="xs" color="gray.500" fontWeight="500">
          Your data is encrypted on our servers.
        </Text>
      </VStack>
    </VStack>
  );
}
